import { assertContentMutationV1, completePrayerDocument } from './content-lifecycle/contracts';
import type { Prayer } from '@/types/prayer';
import type { PrayerAttemptV1, PrayerIntent } from '@/types/prayer-command';
import { materializeProgressEntryIds, pendingTokenForEntry } from './progress-entry-identity';
import { invalidateRawPrayerEdit, removeProgressEntry, rewriteProgressEntry } from './prayer-edit-safety';

export const PRAYER_ATTEMPT_PREFIX = 'prayer-authoring-attempt-v1:';
export function prayerAttemptKey(ownerId: string, commandId: string): string {
  return `${PRAYER_ATTEMPT_PREFIX}${ownerId}:${commandId}`;
}
export class PrayerIntentConflictError extends Error {
  constructor() { super('prayer-intent-conflict'); this.name = 'PrayerIntentConflictError'; }
}
function requireMatch(value: boolean): void { if (!value) throw new PrayerIntentConflictError(); }

/** Field semantics run against current durable truth, never the React mirror. */
export function applyPrayerIntent(current: Prayer, intent: Exclude<PrayerIntent, { kind: 'create' }>, now: string): Prayer {
  let next = materializeProgressEntryIds(current);
  switch (intent.kind) {
    case 'raw':
      requireMatch(current.rawText === intent.expected && current.pendingTranscriptionId === intent.pendingQueueId);
      next = invalidateRawPrayerEdit(next, intent.text, now);
      if (intent.pendingQueueId) next = { ...next, pendingTranscriptionId: undefined, transcriptionFailed: undefined };
      break;
    case 'who':
      requireMatch((current.parsed?.who ?? null) === intent.expected && Boolean(current.userEditedFields?.who) === intent.expectedManual);
      next = { ...next, parsed: { ...(next.parsed ?? { core_prayer: '', suggestedStatus: null }), who: intent.who },
        userEditedFields: { ...next.userEditedFields, who: true } };
      break;
    case 'testimony':
      requireMatch((current.testimony ?? null) === intent.expected);
      if (intent.answered) {
        requireMatch(current.status === intent.answered.expectedStatus && Boolean(current.userEditedFields?.status) === intent.answered.expectedManual);
        next = { ...next, status: 'Answered', userEditedFields: { ...next.userEditedFields, status: true } };
      }
      next = { ...next, testimony: intent.text };
      break;
    case 'append':
      // Identity, not text, distinguishes intentional repetitions. Existing ID
      // is refused even if its words match: only the attempt transaction proves consumption.
      requireMatch(!next.progressHistory.some(entry => entry.id === intent.entry.id));
      next = { ...next, progressHistory: [intent.entry, ...next.progressHistory], pendingEnrichment: pendingTokenForEntry(intent.entry.id, now) };
      break;
    case 'entry-edit': {
      const edited = rewriteProgressEntry(next, intent.entry, intent.text, now);
      requireMatch(edited !== null); next = edited!; break;
    }
    case 'entry-remove': {
      const edited = removeProgressEntry(next, intent.entry, now);
      requireMatch(edited !== null); next = edited!; break;
    }
    case 'status':
      requireMatch(current.status === intent.expected && Boolean(current.userEditedFields?.status) === intent.expectedManual);
      if (intent.movingEntry) {
        const target = intent.movingEntry;
        requireMatch(next.progressHistory.some(entry => entry.id === target.id && entry.note === target.note && entry.date === target.date));
        next = { ...next, progressHistory: next.progressHistory.map(entry => entry.id === target.id ? { ...entry, recommendedStage: 'God is moving' } : entry) };
      }
      next = { ...next, status: intent.status, userEditedFields: { ...next.userEditedFields, status: true } };
      break;
    case 'privacy':
      requireMatch(Boolean(current.isPrivate) === intent.expected);
      next = { ...next, isPrivate: intent.value || undefined };
      break;
  }
  next = { ...next, updatedAt: now };
  // Freeze historical anchors from the authoritative pre-edit document.
  next.answeredAt = next.status === 'Answered'
    ? current.answeredAt ?? (current.status === 'Answered' ? current.updatedAt : now)
    : null;
  next.testimonyWrittenAt = next.testimony?.trim()
    ? current.testimonyWrittenAt ?? (current.testimony?.trim() ? current.updatedAt : now)
    : null;
  if (next.pendingEnrichment && (typeof next.pendingEnrichment === 'string' || next.pendingEnrichment.sourceUpdatedAt !== now)) next.pendingEnrichment = null;
  return next;
}

export function parsePrayerAttemptRow(row: unknown): PrayerAttemptV1 | null {
  if (!row || typeof row !== 'object' || !('key' in row) || !('value' in row) || typeof row.value !== 'string') return null;
  try {
    const value = JSON.parse(row.value) as PrayerAttemptV1;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (value.schema !== 'adia-prayer-attempt-v1' || !uuid.test(value.ownerId) || !uuid.test(value.commandId) || !uuid.test(value.prayerId) || !Number.isSafeInteger(value.revision) || value.revision < 1 || !Number.isFinite(Date.parse(value.authoredAt)) || !Array.isArray(value.recoveredAudio) || row.key !== prayerAttemptKey(value.ownerId, value.commandId)) return null;
    const intent = value.intent;
    if (!intent || typeof intent !== 'object') return null;
    const text = (input: unknown, cap = 250_000): input is string => typeof input === 'string' && input.length <= cap;
    const nullableText = (input: unknown): boolean => input === null || text(input);
    const bool = (input: unknown) => typeof input === 'boolean';
    const status = (input: unknown) => ['Open','In Progress','Answered','Moving'].includes(String(input));
    const entry = (input: unknown): boolean => {
      if (!input || typeof input !== 'object') return false;
      const row = input as Record<string, unknown>;
      return text(row.id, 256) && row.id.trim().length > 0 && typeof row.date === 'string' && Number.isFinite(Date.parse(row.date)) && text(row.note, 50_000);
    };
    switch (intent.kind) {
      case 'create':
        if (!intent.prayer || typeof intent.prayer !== 'object' || intent.prayer.id !== value.prayerId || intent.prayer.pendingTranscriptionId) return null;
        assertContentMutationV1({ schema: 'adia-content-mutation-v1', protocolVersion: 1, ownerId: value.ownerId,
          entityType: 'prayer', entityId: value.prayerId, opId: value.commandId, opKind: 'create', mutationSeq: '1', expectedBaseRevision: null,
          payload: { kind: 'live', document: completePrayerDocument(intent.prayer) } });
        break;
      case 'raw': if (!text(intent.expected) || !text(intent.text) || (intent.pendingQueueId !== undefined && !uuid.test(intent.pendingQueueId))) return null; break;
      case 'who': if (!nullableText(intent.expected) || !text(intent.who, 10_000) || !intent.who.trim() || !bool(intent.expectedManual)) return null; break;
      case 'testimony':
        if (!nullableText(intent.expected) || !nullableText(intent.text) || (intent.answered !== undefined && (!intent.answered || !status(intent.answered.expectedStatus) || !bool(intent.answered.expectedManual)))) return null;
        break;
      case 'append': if (!entry(intent.entry)) return null; break;
      case 'entry-edit': if (!entry(intent.entry) || !text(intent.text, 50_000)) return null; break;
      case 'entry-remove': if (!entry(intent.entry)) return null; break;
      case 'status': if (!status(intent.expected) || !status(intent.status) || !bool(intent.expectedManual) || (intent.movingEntry !== undefined && !entry(intent.movingEntry))) return null; break;
      case 'privacy': if (!bool(intent.expected) || !bool(intent.value)) return null; break;
      default: return null;
    }
    if (value.recoveredAudio.length > 100 || new Set(value.recoveredAudio.map(claim => claim?.queueItemId)).size !== value.recoveredAudio.length) return null;
    for (const claim of value.recoveredAudio) {
      if (!claim || typeof claim !== 'object' || !uuid.test(claim.queueItemId)) return null;
      if (!['inline-prayer','inline-bookmark','progress-update','testimony','typed-replacement'].includes(claim.recoveryKind)) return null;
      if (claim.recoveryKind !== 'typed-replacement' && (!text(claim.expectedTranscribedText) || !claim.expectedTranscribedText.trim())) return null;
      if (claim.recoveryKind === 'progress-update' && (!text(claim.progressEntryId, 256) || !claim.progressEntryId.trim())) return null;
    }
    return value;
  } catch { return null; }
}

export function prayerIntentDraft(intent: PrayerIntent): string {
  switch (intent.kind) {
    case 'create': return intent.prayer.rawText;
    case 'raw': case 'testimony': case 'entry-edit': return intent.text ?? '';
    case 'who': return intent.who;
    case 'append': return intent.entry.note;
    case 'entry-remove': return intent.entry.note;
    case 'status': return intent.status;
    case 'privacy': return String(intent.value);
  }
}

/** Explicit Keep mine uses only the current version that the owner reviewed. */
export function reviewPrayerIntent(intent: PrayerIntent, current: Prayer): PrayerIntent | null {
  switch (intent.kind) {
    case 'create': return null;
    case 'raw': return current.pendingTranscriptionId === intent.pendingQueueId
      ? { ...intent, expected: current.rawText } : null;
    case 'who': return { ...intent, expected: current.parsed?.who ?? null, expectedManual: Boolean(current.userEditedFields?.who) };
    case 'testimony': return { ...intent, expected: current.testimony ?? null,
      ...(intent.answered ? { answered: { expectedStatus: current.status, expectedManual: Boolean(current.userEditedFields?.status) } } : {}) };
    case 'privacy': return { ...intent, expected: Boolean(current.isPrivate) };
    case 'append': return intent;
    case 'status': {
      const entry = intent.movingEntry && materializeProgressEntryIds(current).progressHistory.find(item => item.id === intent.movingEntry?.id);
      if (intent.movingEntry && !entry) return null;
      return { ...intent, expected: current.status, expectedManual: Boolean(current.userEditedFields?.status),
        ...(entry?.id ? { movingEntry: { id: entry.id, note: entry.note, date: entry.date } } : {}) };
    }
    case 'entry-edit': case 'entry-remove': {
      const entry = materializeProgressEntryIds(current).progressHistory.find(item => item.id === intent.entry.id);
      return entry?.id ? { ...intent, entry: { id: entry.id, date: entry.date, note: entry.note } } : null;
    }
  }
}
export function prayerIntentCurrentText(intent: PrayerIntent, current: Prayer): string {
  switch (intent.kind) {
    case 'raw': case 'create': return current.rawText;
    case 'who': return current.parsed?.who ?? '';
    case 'testimony': return current.testimony ?? '';
    case 'append': return current.progressHistory[0]?.note ?? '';
    case 'entry-edit': case 'entry-remove': return materializeProgressEntryIds(current).progressHistory.find(entry => entry.id === intent.entry.id)?.note ?? '';
    case 'status': return current.status;
    case 'privacy': return String(Boolean(current.isPrivate));
  }
}

export function notifyPrayerAttemptsChanged(): void {
  if (typeof window !== 'undefined') setTimeout(() => {
    try { window.dispatchEvent(new Event('adia:prayer-attempts-changed')); } catch { /* observer only */ }
  }, 250);
}
